import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ENTERPRISE_PLAN_ID } from '@/hooks/useOrgPlan';
import { getContractStatus, type ContractStatus } from '@/lib/orgContract';

export interface EnterpriseLicenseStats {
  /** Licencias Enterprise activas asignadas a personas de la organización */
  used: number;
  /** Cupo contratado (organizations.max_users); 0 = sin contrato */
  max: number;
  organizationName: string | null;
  contractStartDate: string | null;
  contractEndDate: string | null;
  contract: ContractStatus;
}

/**
 * Estadísticas de licencias Enterprise y vigencia del contrato de una organización.
 */
export function useEnterpriseLicenseStats(organizationId: string | null | undefined) {
  return useQuery<EnterpriseLicenseStats | null>({
    queryKey: ['enterprise-license-stats', organizationId],
    queryFn: async () => {
      const [orgRes, profilesRes] = await Promise.all([
        supabase
          .from('organizations')
          .select('id, name, max_users, contract_start_date, contract_end_date')
          .eq('id', organizationId!)
          .maybeSingle(),
        supabase
          .from('profiles')
          .select('id')
          .eq('organization_id', organizationId!),
      ]);
      if (orgRes.error) throw orgRes.error;
      if (profilesRes.error) throw profilesRes.error;

      const userIds = (profilesRes.data ?? []).map((p) => p.id);
      let used = 0;
      if (userIds.length > 0) {
        const { count, error } = await supabase
          .from('user_subscriptions')
          .select('id', { count: 'exact', head: true })
          .in('user_id', userIds)
          .eq('plan_id', ENTERPRISE_PLAN_ID)
          .eq('status', 'active');
        if (error) throw error;
        used = count ?? 0;
      }

      const org: any = orgRes.data;
      const rawMax = org?.max_users;
      return {
        used,
        max: rawMax === null || rawMax === undefined ? 0 : rawMax,
        organizationName: org?.name ?? null,
        contractStartDate: org?.contract_start_date ?? null,
        contractEndDate: org?.contract_end_date ?? null,
        contract: getContractStatus(org?.contract_start_date, org?.contract_end_date),
      };
    },
    enabled: !!organizationId,
    staleTime: 60 * 1000,
  });
}
